/* eslint-disable new-cap */
/**
 * REST API を使用したメール取得 hook
 * OpenAPI仕様から生成された型を使用（SSOT）
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AgeRangeInput,
  EmailSearchInput,
  GenderType as GenderTypeType,
  HumanResourceFilter,
  HumanResourceTypes,
  MatterFilter,
  OrderType as OrderTypeType,
  RecruitmentType as RecruitmentTypeType,
  RewardRangeInput,
} from "../api";
import { api } from "../api/client";
import type { paths } from "../api/schema";

// =====================================
// 値として使用可能なオブジェクト（既存コードとの互換性）
// 型は生成されたものを使用
// =====================================

export const RecruitmentType = {
  Matter: "Matter" as const,
  HumanResource: "HumanResource" as const,
};
export type RecruitmentType = RecruitmentTypeType;

export const OrderType = {
  DateAsc: "DateAsc" as const,
  DateDesc: "DateDesc" as const,
  AgeAsc: "AgeAsc" as const,
  AgeDesc: "AgeDesc" as const,
  RewardAsc: "RewardAsc" as const,
  RewardDesc: "RewardDesc" as const,
};
export type OrderType = OrderTypeType;

export const GenderType = {
  Male: "Male" as const,
  Female: "Female" as const,
};
export type GenderType = GenderTypeType;

// =====================================
// Input Types を再エクスポート（SSOT）
// =====================================

export type {
  AgeRangeInput,
  RewardRangeInput,
  HumanResourceTypes,
  HumanResourceFilter,
  MatterFilter,
  EmailSearchInput,
};

// APIレスポンスの型を取得
type EmailsApiResponse =
  paths["/api/emails"]["get"]["responses"]["200"]["content"]["application/json"];
type EmailDocumentApi = EmailsApiResponse["nodes"][number];

// =====================================
// Entity Types（生成された型を再エクスポート）
// =====================================

export type EmailFrom = EmailDocumentApi["from"];
export type HumanResourceColumn = NonNullable<EmailDocumentApi["hrColumn"]>;
export type MatterColumn = NonNullable<EmailDocumentApi["matterColumn"]>;

// EmailDocument型（既存コードとの互換性のため）
export interface EmailDocument {
  id: string;
  date: string;
  subject: string;
  content: string;
  from: EmailFrom;
  recruitmentType: RecruitmentType;
  hrColumn?: HumanResourceColumn | null;
  matterColumn?: MatterColumn | null;
}

export interface EmailDocuments {
  totalCount: number;
  nodes: EmailDocument[];
}

// =====================================
// REST API Hook Types
// =====================================

export interface EmailsResponse {
  emails: EmailDocuments;
}

export interface UseEmailsRestApiOptions {
  variables: {
    filter: EmailSearchInput | null;
    offset: number;
    limit: number;
  };
  onCompleted?: () => void;
  notifyOnNetworkStatusChange?: boolean;
}

export interface UseEmailsRestApiResult {
  data: EmailsResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: (variables?: {
    filter: EmailSearchInput | null;
    offset: number;
    limit: number;
  }) => Promise<EmailsResponse>;
}

/**
 * APIレスポンスをEmailDocument形式に変換
 * @param {EmailDocumentApi} doc - APIからのメールドキュメント
 * @return {EmailDocument} 変換されたメールドキュメント
 */
function toEmailDocument(doc: EmailDocumentApi): EmailDocument {
  return {
    ...doc,
    recruitmentType: doc.recruitmentType as RecruitmentType,
  };
}

/**
 * メール一覧を REST API から取得する hook
 * @param {UseEmailsRestApiOptions} options - フック設定オプション
 * @return {UseEmailsRestApiResult} メールデータ、エラー、ローディング状態、再取得関数を含むオブジェクト
 */
export function useEmailsRestApi(
  options: UseEmailsRestApiOptions,
): UseEmailsRestApiResult {
  const [data, setData] = useState<EmailsResponse | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchEmails = useCallback(
    async (variables: {
      filter: EmailSearchInput | null;
      offset: number;
      limit: number;
    }): Promise<EmailsResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const params: Record<string, string> = {
          offset: String(variables.offset),
          limit: String(variables.limit),
        };

        if (variables.filter) {
          params.filter = JSON.stringify(variables.filter);
        }

        const { data: responseData, error: apiError } = await api.GET(
          "/api/emails",
          {
            params: { query: params },
          },
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || "API error",
          );
        }

        if (!responseData) {
          throw new Error("No data received");
        }

        const result: EmailsResponse = {
          emails: {
            totalCount: responseData.totalCount,
            nodes: responseData.nodes.map(toEmailDocument),
          },
        };

        setData(result);
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
        options.onCompleted?.();
      }
    },
    [options.onCompleted],
  );

  const initialFetchDone = useRef(false);
  const variablesRef = useRef(options.variables);

  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchEmails(variablesRef.current).catch(() => {
        // エラーは state で管理
      });
    }
  }, [fetchEmails]);

  const refetch = useCallback(
    async (variables?: {
      filter: EmailSearchInput | null;
      offset: number;
      limit: number;
    }): Promise<EmailsResponse> => {
      return fetchEmails(variables || options.variables);
    },
    [fetchEmails, options.variables],
  );

  return {
    data,
    error,
    loading,
    refetch,
  };
}

export default useEmailsRestApi;
